import type { QueryKey } from "@tanstack/react-query";

import { getListDevicesQueryOptions } from "@/api/orval/generated/device";
import type { Device } from "@/api/orval/generated/schemas/device";
import type { SensoterraDevice } from "@/api/orval/generated/schemas/sensoterraDevice";
import type { ThirdPartyDevice } from "@/api/orval/generated/schemas/thirdPartyDevice";
import { getListSensoterraDevicesQueryOptions } from "@/api/orval/generated/sensoterra-device";
import { getListThirdPartyDevicesQueryOptions } from "@/api/orval/generated/third-party-device";
import { getConnectedZones } from "@/services/device-attributes/attributes/general";
import type { LegacyEntityIndex } from "@/services/map-management/legacy-entity-index";
import type MapEntityBase from "@/services/map-management/map-entities/map-entity-base";

import { isMigratedDeviceType } from "./migrated-device-types";
import { toWeatherStationMarker } from "./projections/to-weather-station-marker";
import {
    hasUsablePosition,
    type LegacyDrawnMarker,
    type MapMarker,
    type MarkerScope,
    type MarkerSource,
    markerIdFor,
} from "./types";

/**
 * Anything a projection reads from outside its own DTOs belongs here rather than being read where it
 * is used — `toMarkers` runs inside a render memo and has to stay pure
 */
export type MarkerSourceContext = {
    entities: LegacyEntityIndex;
    areWarningsHiddenByUser: boolean;
    areWeatherStationsVisible: boolean;
};

export type MarkerSourceDescriptor = {
    source: MarkerSource;
    queryOptions: () => { queryKey: QueryKey };
    ownsEntity: (mapEntity: MapEntityBase) => boolean;
    /**
     * Deliberately not generic in its DTO: each source owns the mapping from its own list's shape,
     * which is the per-list difference the descriptor exists to absorb
     */
    toMarkers: (dtos: readonly unknown[], context: MarkerSourceContext) => (MapMarker | LegacyDrawnMarker)[];
};

/**
 * Which model, and where its document says it sits — every source's DTO reduced to the same shape,
 * whatever fields it kept those in. Coordinates stay nullable, because a legacy document's can be.
 */
type ModelPosition = { modelId: string; lat: number | null | undefined; long: number | null | undefined };

/** A legacy entity's zone membership, as the scope the dimming rule reads */
const scopeFromEntity = (mapEntity: MapEntityBase): MarkerScope => ({
    covers: "zones",
    zoneIds: getConnectedZones({ model: mapEntity.model }).map((zone) => zone.id),
});

/** Pairs one source's positions to the MapEntities that draw them, dropping any that cannot be drawn */
const pairMarkers = (
    source: MarkerSource,
    positions: readonly ModelPosition[],
    entities: LegacyEntityIndex,
    ownsEntity: (mapEntity: MapEntityBase) => boolean,
): LegacyDrawnMarker[] =>
    positions.flatMap(({ modelId, lat, long }) => {
        if (!hasUsablePosition({ lat, long })) return [];

        const mapEntity = entities[modelId];
        if (!mapEntity || !ownsEntity(mapEntity)) return [];

        return [
            {
                id: markerIdFor(source, modelId),
                modelId,
                source,
                drawnBy: "legacy" as const,
                scope: scopeFromEntity(mapEntity),
                lat: lat as number,
                long: long as number,
                mapEntity,
            },
        ];
    });

/**
 * True when this MapEntity was constructed from a `listDevices` `msense` DTO.
 *
 * SessionHandler builds those as `SensoterraMoistureSensor` (category `SensoterraMoisture`), the same
 * class as a real probe from `listSensoterraDevices`. The DTO's `DeviceType` is what distinguishes them:
 * only the devices-list constructor branch lowercases `DeviceType` and matches `"msense"`.
 */
const isMsenseFromDevicesList = (mapEntity: MapEntityBase) =>
    String(mapEntity.model?._lastJSON?.DeviceType ?? "").toLowerCase() === "msense";

/** The categories owned outright; `msense` probes are owned through the predicate below instead */
const DEVICE_CATEGORIES = new Set(["irrigationDevice", "blockValve"]);

const ownsDeviceEntity = (mapEntity: MapEntityBase) =>
    DEVICE_CATEGORIES.has(mapEntity.model?.category) || isMsenseFromDevicesList(mapEntity);

/** The `listDevices` query: irrigation devices, block valves, and Sensoterra-backed `msense` probes */
const deviceSource: MarkerSourceDescriptor = {
    source: "device",
    queryOptions: getListDevicesQueryOptions,
    ownsEntity: ownsDeviceEntity,
    toMarkers: (dtos, { entities }) =>
        pairMarkers(
            "device",
            (dtos as readonly Device[]).map((dto) => ({
                modelId: dto.node_id || dto._id,
                lat: dto.latitude,
                long: dto.longitude,
            })),
            entities,
            ownsDeviceEntity,
        ),
};

const ownsThirdPartyEntity = (mapEntity: MapEntityBase) => mapEntity.model?.category === "thirdPartyDevice";

/**
 * The `listThirdPartyDevices` query. Pairs on `node_id`; position is nested rather than flat here.
 *
 * This list is split between the two ways a marker can be drawn: a device type the v2 layer owns
 * draws itself, everything else pairs to a legacy entity.
 */
const thirdPartySource: MarkerSourceDescriptor = {
    source: "thirdPartyDevice",
    queryOptions: getListThirdPartyDevicesQueryOptions,
    ownsEntity: ownsThirdPartyEntity,
    toMarkers: (dtos, { entities, areWarningsHiddenByUser, areWeatherStationsVisible }) => {
        // Dropped before the split below, so a hidden station gets no marker from either half
        const all = (dtos as readonly ThirdPartyDevice[]).filter(
            (dto) => !dto.deleted && (areWeatherStationsVisible || dto.deviceType !== "davisWeatherStation"),
        );
        // One predicate, both halves, so they cannot drift into claiming the same document or neither
        const drawsItself = (dto: ThirdPartyDevice) => isMigratedDeviceType(dto.deviceType);

        const legacyPairedMarkers = pairMarkers(
            "thirdPartyDevice",
            all
                .filter((dto) => !drawsItself(dto))
                .map((dto) => ({
                    modelId: dto.node_id,
                    lat: dto.position?.lat,
                    long: dto.position?.lng,
                })),
            entities,
            ownsThirdPartyEntity,
        );
        const v2Markers = all
            .filter(drawsItself)
            .flatMap((dto) => toWeatherStationMarker(dto, { areWarningsHiddenByUser }) ?? []);
        return [...legacyPairedMarkers, ...v2Markers];
    },
};

/** Does not own devices-list `msense` probes: those share this model class but live in `listDevices` */
const ownsSensoterraEntity = (mapEntity: MapEntityBase) =>
    mapEntity.model?.category === "SensoterraMoisture" && !isMsenseFromDevicesList(mapEntity);

/**
 * The `listSensoterraDevices` query. Pairs on `_id`, matching `SensoterraMoistureSensor`'s `dbID`.
 *
 * The poll never patches this one (`refresh-devices.ts` fans out devices and third-party only), so a
 * probe moved elsewhere is picked up on the next login or legacy write.
 */
const sensoterraSource: MarkerSourceDescriptor = {
    source: "sensoterraDevice",
    queryOptions: getListSensoterraDevicesQueryOptions,
    ownsEntity: ownsSensoterraEntity,
    toMarkers: (dtos, { entities }) =>
        pairMarkers(
            "sensoterraDevice",
            (dtos as readonly SensoterraDevice[]).map((dto) => ({
                modelId: dto._id,
                lat: dto.latitude,
                long: dto.longitude,
            })),
            entities,
            ownsSensoterraEntity,
        ),
};

/** The map's marker sources, in the order `useMapMarkers` queries them */
export const MARKER_SOURCES: readonly MarkerSourceDescriptor[] = [deviceSource, thirdPartySource, sensoterraSource];

/** The v2 list a legacy entity's writes should stale, or null if it is outside this tier */
export const queryKeyForEntity = (mapEntity: MapEntityBase) =>
    MARKER_SOURCES.find((source) => source.ownsEntity(mapEntity))?.queryOptions().queryKey ?? null;
